import { useState, useEffect, useRef } from 'react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Mo','Tu','We','Th','Fr','Sa','Su'];

function fmtDisplay(ds) {
  if (!ds) return '';
  const d = new Date(ds + 'T12:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtISO(year, month, day) {
  return `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
}

function buildCells(year, month) {
  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
  const offset   = (firstDow + 6) % 7;               // Mon=0 … Sun=6
  const total    = new Date(year, month + 1, 0).getDate();
  const cells    = Array(offset).fill(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  return cells;
}

export default function DateRangePicker({
  startDate,   // 'YYYY-MM-DD' or null
  endDate,     // 'YYYY-MM-DD' or null
  onChange,    // (startDate, endDate) => void
  minDate,     // 'YYYY-MM-DD' — default today
  labelStart = 'Pickup Date',
  labelEnd   = 'Return Date',
}) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const minStr   = minDate || todayStr;

  const now = new Date();
  const [open,      setOpen]      = useState(false);
  const [selecting, setSelecting] = useState('start');
  const [hover,     setHover]     = useState(null);
  const [viewYear,  setViewYear]  = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const ref = useRef(null);

  /* close on outside click */
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* jump to relevant month when opening */
  function openFor(mode) {
    setSelecting(mode);
    const base = mode === 'end' && startDate ? startDate : (startDate || todayStr);
    const d = new Date(base + 'T12:00');
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setOpen(true);
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  function selectDay(ds) {
    if (selecting === 'start') {
      onChange(ds, null);       // reset end, keep start
      setSelecting('end');      // auto-advance
    } else {
      if (startDate && ds < startDate) {
        // clicked before start → restart selection
        onChange(ds, null);
        setSelecting('end');
      } else {
        onChange(startDate, ds);
        setOpen(false);
        setHover(null);
      }
    }
  }

  /* range helpers */
  const effectiveEnd = selecting === 'end' && hover ? hover : endDate;

  function isRangeStart(ds) { return ds === startDate; }
  function isRangeEnd(ds)   { return ds === effectiveEnd; }
  function inRange(ds) {
    if (!startDate || !effectiveEnd) return false;
    return ds > startDate && ds < effectiveEnd;
  }

  const cells    = buildCells(viewYear, viewMonth);
  const dayCount = startDate && endDate
    ? Math.round((new Date(endDate) - new Date(startDate)) / 86400000)
    : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>

      {/* ── Trigger row ── */}
      <div style={{ display: 'flex', gap: 8 }}>
        {[
          { mode: 'start', label: labelStart, val: startDate },
          { mode: 'end',   label: labelEnd,   val: endDate   },
        ].map(({ mode, label, val }) => {
          const active = open && selecting === mode;
          return (
            <div
              key={mode}
              onClick={() => openFor(mode)}
              style={{
                flex: 1,
                border: `1.5px solid ${active ? 'var(--ac)' : 'var(--bd)'}`,
                borderRadius: 'var(--r)',
                padding: '9px 12px',
                cursor: 'pointer',
                background: active ? 'var(--ac-l)' : 'var(--sur)',
                transition: 'border-color .15s, background .15s',
              }}
            >
              <div style={{ fontSize: 9, fontWeight: 700, color: active ? 'var(--ac)' : 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 3 }}>
                {mode === 'start' ? '↑' : '↓'} {label}
              </div>
              <div style={{ fontSize: 13, color: val ? 'var(--tx)' : 'var(--tx3)', fontWeight: val ? 500 : 400 }}>
                {val ? fmtDisplay(val) : 'Select date…'}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Calendar popup ── */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0,
          background: 'var(--sur)', border: '1px solid var(--bd)',
          borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,.16)',
          zIndex: 9999, padding: 16, minWidth: 300,
        }}>

          {/* month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <button onClick={prevMonth} style={{ background: 'none', border: '1px solid var(--bd)', borderRadius: 6, cursor: 'pointer', padding: '2px 10px', fontSize: 15, color: 'var(--tx)', lineHeight: 1.4 }}>‹</button>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{MONTHS[viewMonth]} {viewYear}</span>
            <button onClick={nextMonth} style={{ background: 'none', border: '1px solid var(--bd)', borderRadius: 6, cursor: 'pointer', padding: '2px 10px', fontSize: 15, color: 'var(--tx)', lineHeight: 1.4 }}>›</button>
          </div>

          {/* pickup / return toggle */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
            {[
              { mode: 'start', label: `↑ ${labelStart}` },
              { mode: 'end',   label: `↓ ${labelEnd}`   },
            ].map(({ mode, label }) => (
              <div
                key={mode}
                onClick={() => setSelecting(mode)}
                style={{
                  flex: 1, textAlign: 'center', fontSize: 10, fontWeight: 700,
                  padding: '5px 0', borderRadius: 6, cursor: 'pointer',
                  background: selecting === mode ? 'var(--ac)' : 'var(--sur2)',
                  color: selecting === mode ? '#fff' : 'var(--tx3)',
                  textTransform: 'uppercase', letterSpacing: '.05em',
                  transition: 'background .15s',
                }}
              >
                {label}
              </div>
            ))}
          </div>

          {/* day-of-week header */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 4 }}>
            {DAYS.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--tx3)', padding: '2px 0' }}>{d}</div>
            ))}
          </div>

          {/* day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1 }}>
            {cells.map((day, i) => {
              if (!day) return <div key={`e${i}`} />;
              const ds      = fmtISO(viewYear, viewMonth, day);
              const isPast  = ds < minStr;
              const isToday = ds === todayStr;
              const isSt    = isRangeStart(ds);
              const isEn    = isRangeEnd(ds);
              const inside  = inRange(ds);
              const isSel   = isSt || isEn;

              let bg = 'transparent';
              let br = '50%';
              let col = isPast ? 'var(--tx3)' : 'var(--tx)';

              if (isSt)    { bg = 'var(--ac)'; br = inside || isEn ? '50% 0 0 50%' : '50%'; col = '#fff'; }
              else if (isEn) { bg = 'var(--ac)'; br = inside || isSt ? '0 50% 50% 0' : '50%'; col = '#fff'; }
              else if (inside) { bg = 'var(--ac-l)'; br = '0'; col = 'var(--ac)'; }
              else if (isToday) { bg = 'transparent'; br = '50%'; }

              return (
                <div
                  key={ds}
                  onClick={() => !isPast && selectDay(ds)}
                  onMouseEnter={() => selecting === 'end' && !isPast && setHover(ds)}
                  onMouseLeave={() => setHover(null)}
                  style={{
                    textAlign: 'center',
                    padding: '7px 0',
                    fontSize: 12,
                    cursor: isPast ? 'default' : 'pointer',
                    color: col,
                    fontWeight: isSel || isToday ? 700 : 400,
                    background: bg,
                    borderRadius: br,
                    outline: isToday && !isSel ? '2px solid var(--ac)' : 'none',
                    outlineOffset: '-2px',
                    opacity: isPast ? 0.3 : 1,
                    transition: 'background .08s',
                    userSelect: 'none',
                  }}
                >
                  {day}
                </div>
              );
            })}
          </div>

          {/* day count summary */}
          {dayCount !== null && dayCount > 0 && (
            <div style={{ marginTop: 10, borderTop: '1px solid var(--bd)', paddingTop: 8, textAlign: 'center', fontSize: 11, color: 'var(--tx3)' }}>
              <span style={{ fontWeight: 600, color: 'var(--ac)' }}>{dayCount}</span> day{dayCount !== 1 ? 's' : ''} rental
              &nbsp;·&nbsp;
              {fmtDisplay(startDate)} → {fmtDisplay(endDate)}
            </div>
          )}

          {/* hint */}
          <div style={{ marginTop: 6, textAlign: 'center', fontSize: 10, color: 'var(--tx3)' }}>
            {selecting === 'start' ? 'Click a pickup date' : 'Click a return date'}
          </div>
        </div>
      )}
    </div>
  );
}
